import { useState, useEffect } from 'react';
import { Filter, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { ConversationFilters as Filters } from '../../services/omnichannelService';
import { httpClient } from '../../services/httpClient';

interface ConversationFiltersProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}

interface Agent {
  id: number;
  name: string;
  email: string;
  active_assignments: number;
  max_concurrent_conversations: number;
  is_available: boolean;
}

interface AgentGroup {
  group_id: number;
  group_name: string;
  agents: Agent[];
}

export default function ConversationFilters({
  filters,
  onFiltersChange
}: ConversationFiltersProps) {
  const { theme } = useTheme();
  const { user } = useAuthContext();
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  const [agentGroups, setAgentGroups] = useState<AgentGroup[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const handleStatusChange = (status: string) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((s) => s !== status)
      : [...currentStatuses, status];
    onFiltersChange({ ...filters, status: newStatuses });
  };

  const handlePriorityChange = (priority: string) => {
    const currentPriorities = filters.priority || [];
    const newPriorities = currentPriorities.includes(priority)
      ? currentPriorities.filter((p) => p !== priority)
      : [...currentPriorities, priority];
    onFiltersChange({ ...filters, priority: newPriorities });
  };

  const handleChannelTypeChange = (channelType: string) => {
    const currentChannels = filters.channel_type || [];
    const newChannels = currentChannels.includes(channelType)
      ? currentChannels.filter((c) => c !== channelType)
      : [...currentChannels, channelType];
    onFiltersChange({ ...filters, channel_type: newChannels });
  };

  const handleAgentChange = (value: string) => {
    if (value === '') {
      // All conversations
      const { assigned_to, is_unassigned, ...rest } = filters;
      onFiltersChange(rest);
    } else if (value === 'unassigned') {
      // Unassigned conversations
      onFiltersChange({ ...filters, assigned_to: undefined, is_unassigned: true });
    } else if (value === 'me') {
      // Assigned to me
      onFiltersChange({ ...filters, assigned_to: user?.id?.toString(), is_unassigned: false });
    } else {
      // Specific agent
      onFiltersChange({ ...filters, assigned_to: value, is_unassigned: false });
    }
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  // Fetch all available agents on mount
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setLoadingAgents(true);
        // Fetch all agents with assignment counts
        const response = await httpClient.get<{ success: boolean; data: any[] }>(
          '/api/channels/agents/all-with-counts'
        );

        // Transform to agent group format
        const agents: Agent[] = (response.data || []).map((user: any) => ({
          id: user.id,
          name: user.full_name,
          email: user.email,
          active_assignments: user.active_assignments || 0,
          max_concurrent_conversations: 10,
          is_available: true
        }));

        setAgentGroups([
          {
            group_id: 0,
            group_name: 'All Agents',
            agents
          }
        ]);
      } catch (error) {
        console.error('Failed to load agents:', error);
        setAgentGroups([]);
      } finally {
        setLoadingAgents(false);
      }
    };

    fetchAgents();
  }, []);

  const hasActiveFilters =
    (filters.status && filters.status.length > 0) ||
    (filters.priority && filters.priority.length > 0) ||
    (filters.channel_type && filters.channel_type.length > 0) ||
    filters.assigned_to ||
    filters.is_unassigned ||
    filters.search;

  // Get current assigned to value for dropdown
  const getAssignedToValue = () => {
    if (filters.is_unassigned) return 'unassigned';
    if (filters.assigned_to === user?.id?.toString()) return 'me';
    return filters.assigned_to || '';
  };

  return (
    <div
      className={`border-b ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}
    >
      <div className="p-3">
        {/* Header - Horizontal Layout */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 text-sm font-medium transition-colors relative ${
              theme === 'dark'
                ? 'text-gray-200 hover:text-white'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className={`flex items-center gap-1 text-xs transition-colors ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-gray-200'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <X className="h-3 w-3" />
              <span>Clear</span>
            </button>
          )}
        </div>

        {/* Search - Compact */}
        <input
          type="text"
          placeholder="Search customers..."
          value={filters.search || ''}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          className={`w-full px-2.5 py-1.5 rounded-md border text-sm transition-colors ${
            theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500 focus:border-gray-500'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-400'
          } focus:outline-none`}
        />

        {/* Filter Options - Compact Grid Layout */}
        {showFilters && (
          <div
            className="mt-3 space-y-3 max-h-[60vh] overflow-y-auto"
            style={{ overflowX: 'hidden' }}
          >
            {/* Status Filter */}
            <div>
              <div
                className={`text-xs font-medium mb-1.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
              >
                Status
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {['pending', 'assigned', 'resolved', 'closed'].map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all capitalize ${
                      filters.status?.includes(status)
                        ? 'bg-blue-500 text-white shadow-sm'
                        : theme === 'dark'
                          ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-700 border border-gray-600/50'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Priority Filter */}
            <div>
              <div
                className={`text-xs font-medium mb-1.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
              >
                Priority
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {['low', 'medium', 'high', 'urgent'].map((priority) => (
                  <button
                    key={priority}
                    onClick={() => handlePriorityChange(priority)}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all capitalize ${
                      filters.priority?.includes(priority)
                        ? 'bg-blue-500 text-white shadow-sm'
                        : theme === 'dark'
                          ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-700 border border-gray-600/50'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {priority}
                  </button>
                ))}
              </div>
            </div>

            {/* Channel Type Filter */}
            <div>
              <div
                className={`text-xs font-medium mb-1.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
              >
                Channel
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {['messenger', 'whatsapp', 'telegram', 'email', 'live_chat'].map((channel) => (
                  <button
                    key={channel}
                    onClick={() => handleChannelTypeChange(channel)}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all capitalize ${
                      filters.channel_type?.includes(channel)
                        ? 'bg-blue-500 text-white shadow-sm'
                        : theme === 'dark'
                          ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-700 border border-gray-600/50'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {channel}
                  </button>
                ))}
              </div>
            </div>

            {/* Assigned To Filter - Updated with "Me" option */}
            <div>
              <div
                className={`text-xs font-medium mb-1.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
              >
                Assigned To
              </div>
              <select
                value={getAssignedToValue()}
                onChange={(e) => handleAgentChange(e.target.value)}
                disabled={loadingAgents}
                className={`w-full px-2.5 py-1.5 rounded-md border text-xs transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-200'
                    : 'bg-white border-gray-300 text-gray-700'
                } ${loadingAgents ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} focus:outline-none focus:ring-1 focus:ring-blue-500`}
              >
                <option value="">All conversations</option>
                <option value="unassigned">Unassigned</option>
                <option value="me">Me</option>
                {agentGroups.map((group) =>
                  group.agents
                    .filter((agent) => agent.id !== user?.id) // Don't show current user twice
                    .map((agent) => (
                      <option key={agent.id} value={agent.id.toString()}>
                        {agent.name} ({agent.active_assignments})
                      </option>
                    ))
                )}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
